import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { coachKpiAPI, workspaceAPI } from '../../services/api';
import { resolvePhotoUrl } from '../../utils/photoUtils';

// 客户跟进提醒条 — what replaced the header notification bell on the coaching
// side. It is deliberately "push" rather than "pull": the coach lands on the
// client list and the four things that need doing today are already on screen,
// instead of sitting behind a bell nobody clicks.
//
// The counts are DERIVED from the client data (打卡的下次联系日期 / 上次联系
// 时间 / 约见时间), not from a notifications table — so an item disappears when
// the work is actually done, not when someone marks it read.
//
// Items are grouped BY DAY (已逾期 / 今天 / 明天 / 之后的日期 / 久未联系) and
// only 今天 is open — the coach sees today's work first and the other days are
// one click away, instead of a wall of names.
//
// Props: isZh, silentDays (默认 7), onOpenClient(client, action) — opens the CRM
// modal in place (action 'contact' → 打卡, 'notes' → 客户情况), refreshKey (bump to
// refetch after a check-in).
//
// 5.0: the same compact day-grouped bar also carries 待办 and 48 小时未联系 (dated
// items, so they land on their day or in 已逾期) and 未约见 (own group, before 久未联系).
//
// Group order: 已逾期 · 今天 · 明天 · (later days) · 待办 · 未约见 ≥7天 · (待审核) ·
// 约会提醒 · 紧急客户 (always last).
//   约会提醒 — c.date_reminders: my members' dates today / tomorrow (Pacific), plus dates
//              in the past 2 days with no 约会反馈 yet (red 「待写约会反馈」). One card per date.
//   紧急客户 — the old 紧急客诉 group (bucket complaint_urgent) merged with manual flags
//              (c.urgent_flag, set from 客户记录 「标记紧急」); note 「紧急 · …」 / 「客诉 · …」.
// Both fields are absent on an older backend — the groups then simply don't appear.
//
// demoRows (操作引导 示例模式): use these rows instead of fetching — the tour's sample members.

// Luxe tokens (theme/luxe.js): hairline, ink, small text, recessed, raised, serif.
const C = { line: 'rgba(17,17,17,.12)', lineSoft: 'rgba(17,17,17,.08)', ink: '#111111', muted: '#6E685F', alt: '#EFEAE2', card: '#FFFDF9', gold: '#8A6D3B', serif: "'Cormorant Garamond', 'Noto Serif SC', 'Songti SC', Georgia, serif" };

// Bucket palette — the bar groups by day, but the LIST's 跟进状态 column still
// badges a row by its most urgent bucket, and both read from here.
const GROUPS = [
  { key: 'overdue', zh: '逾期未联系', en: 'Overdue', color: '#9E2B25', bg: 'rgba(158,43,37,.09)' },
  { key: 'week', zh: '本周要联系', en: 'Due this week', color: '#8A6D3B', bg: 'rgba(138,109,59,.12)' },
  { key: 'meeting', zh: '今明约见', en: 'Meeting soon', color: '#111111', bg: '#EFEAE2' },
  { key: 'silent', zh: '久未联系', en: 'Gone quiet', color: '#6E685F', bg: 'rgba(17,17,17,.05)' },
];

const dayDiff = (a, b) => Math.round((a - b) / 86400000);
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

// reviewQueue (主管 roles): also show 「待审核 N」 — members with records waiting for this reviewer
// (/service/review-queue), in the same chip + card design; a card opens 客户工作台 ⑤ 审核.
export default function CoachFollowupBar({ isZh = true, silentDays = 7, onOpenClient, onData, refreshKey = 0, demoRows = null, reviewQueue = false }) {
  const [clients, setClients] = useState([]);
  const [reviews, setReviews] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [openDay, setOpenDay] = useState(null); // expanded day key (later days start collapsed)
  const t = (zh, en) => (isZh ? zh : en);

  const load = useCallback(async () => {
    if (Array.isArray(demoRows)) { setClients(demoRows); setLoading(false); return; }
    setLoading(true);
    try {
      const r = await coachKpiAPI.getFollowupAlerts({ silent_days: silentDays });
      const rows = Array.isArray(r.data?.clients) ? r.data.clients : [];
      setClients(rows);
      // The list's 跟进状态 column reads the same rows, so a row badge and this
      // bar are always the same answer.
      if (onData) onData(rows);
    } catch (e) {
      setClients([]);
      if (onData) onData([]);
    } finally {
      setLoading(false);
    }
  }, [silentDays, onData, demoRows]);

  useEffect(() => {
    if (!reviewQueue || Array.isArray(demoRows)) { setReviews([]); return undefined; }
    let alive = true;
    workspaceAPI.reviewQueue()
      .then(r => { if (alive) setReviews(Array.isArray(r.data?.items) ? r.data.items : []); })
      .catch(() => { if (alive) setReviews([]); });
    return () => { alive = false; };
  }, [reviewQueue, demoRows, refreshKey]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // One client can owe two things (a contact due today AND a date tomorrow), so
  // the unit here is an ITEM — client × what's due × when — grouped by the day
  // it is due and sorted by time inside the day. 久未联系 has no date of its
  // own; it trails the dated groups.
  const days = useMemo(() => {
    const today = startOfDay(new Date());
    const items = [];
    clients.forEach(c => {
      const bs = c.buckets || [];
      if ((bs.includes('overdue') || bs.includes('week')) && c.next_contact_at) {
        items.push({ c, kind: 'contact', at: new Date(c.next_contact_at) });
      }
      if (bs.includes('meeting') && c.next_meeting_at) {
        items.push({ c, kind: 'meeting', at: new Date(String(c.next_meeting_at).replace(' ', 'T')) });
      }
      // Only when nothing is scheduled — otherwise the client already appears
      // on a real day and would be listed twice.
      if (bs.includes('silent') && !c.next_contact_at && !(bs.includes('meeting') && c.next_meeting_at)) {
        items.push({ c, kind: 'silent', at: null });
      }
      // 紧急客户 = 紧急客诉 + manual urgent flag, one card per client.
      if (bs.includes('complaint_urgent') || c.urgent_flag) items.push({ c, kind: 'urgent', at: null, ord: c.urgent_flag ? 0 : 1 });
      // 约会提醒: upcoming first (by time), then the ones waiting for 约会反馈.
      (Array.isArray(c.date_reminders) ? c.date_reminders : []).forEach(dr => {
        if (!dr || !dr.date) return;
        const ts = new Date(`${dr.date}T${dr.time || '00:00'}:00`).getTime() || 0;
        items.push({ c, kind: 'date', at: null, dr, ord: (dr.feedback_due ? 1e15 : 0) + ts });
      });
      if (bs.includes('todo')) items.push({ c, kind: 'todo', at: c.todo_due_at ? new Date(c.todo_due_at) : new Date() });
      if (bs.includes('no_contact')) items.push({ c, kind: 'nocontact', at: c.next_due_at ? new Date(c.next_due_at) : new Date() });
      if (bs.includes('no_meeting')) items.push({ c, kind: 'nomeeting', at: null });
    });
    reviews.forEach(rv => {
      if (!(Number(rv.pending_total) > 0 || Number(rv.rectifications_to_verify) > 0)) return;
      const parts = (rv.name || '').split(' ');
      items.push({
        kind: 'review', at: null, rv,
        c: { profile_id: rv.profile_id, first_name: parts[0] || rv.name || '', last_name: parts.slice(1).join(' '),
          offline_id: rv.offline_id, profile_photo: rv.photo },
      });
    });

    // Chip palette per day group (Luxe): 逾期 danger / 今天 gold / 之后 ink / 久未联系 muted.
    const TONE = {
      overdue: { color: '#9E2B25', bg: 'rgba(158,43,37,.09)' },
      today: { color: '#8A6D3B', bg: 'rgba(138,109,59,.12)' },
      later: { color: '#111111', bg: '#EFEAE2' },
      silent: { color: '#6E685F', bg: 'rgba(17,17,17,.05)' },
      review: { color: '#3A3835', bg: '#EFEAE2' },
      urgent: { color: '#FFFDF9', bg: '#9E2B25' },
      todo: { color: '#3A3835', bg: 'rgba(138,109,59,.08)' },
      date: { color: '#2F5E44', bg: 'rgba(63,125,90,.10)' },
    };
    const map = new Map(); // key -> { key, label, sort, color, bg, items[] }
    const put = (key, label, sort, tone, item) => {
      let g = map.get(key);
      if (!g) { g = { key, label, sort, color: tone.color, bg: tone.bg, items: [] }; map.set(key, g); }
      g.items.push(item);
    };
    const dayLabel = (d) => {
      const diff = dayDiff(startOfDay(d), today);
      if (diff === 0) return t('今天', 'Today');
      if (diff === 1) return t('明天', 'Tomorrow');
      const wd = isZh ? ['周日', '周一', '周二', '周三', '周四', '周五', '周六'][d.getDay()]
        : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()];
      return isZh ? `${d.getMonth() + 1}月${d.getDate()}日 ${wd}` : `${wd} ${d.getMonth() + 1}/${d.getDate()}`;
    };

    items.forEach(item => {
      // Group order: 已逾期 → 今天 → 明天 / later days → 待办 → 未约见 ≥7天 → (待审核) → 约会提醒 → 紧急客户.
      if (item.kind === 'review') {
        put('review', t('待审核', 'To review'), 4.3e12, TONE.review, item);
        return;
      }
      if (item.kind === 'date') {
        put('date', t('约会提醒', 'Dates'), 4.6e12, TONE.date, item);
        return;
      }
      if (item.kind === 'urgent') {
        put('urgent', t('紧急客户', 'Urgent clients'), 5e12, TONE.urgent, item);
        return;
      }
      if (item.kind === 'todo') {
        put('todo', t('待办', 'To-do'), 3.8e12, TONE.todo, item);
        return;
      }
      if (item.kind === 'silent' || item.kind === 'nomeeting') {
        // 久未联系 and 未约见 share one group (owner's wording: 未约见 ≥7天); one row per client.
        const g = map.get('silent');
        if (g && g.items.some(x => x.c.profile_id === item.c.profile_id)) return;
        put('silent', t(`未约见 ≥${silentDays}天`, `No meeting ${silentDays}d+`), 4e12, TONE.silent, item);
        return;
      }
      const d = startOfDay(item.at);
      if (d < today) {
        // Everything past due collapses into ONE group — a coach works it as a
        // single backlog, not day by day.
        put('overdue', t('已逾期', 'Overdue'), -1, TONE.overdue, item);
        return;
      }
      const key = `d${d.getTime()}`;
      put(key, dayLabel(item.at), d.getTime(), dayDiff(d, today) === 0 ? TONE.today : TONE.later, item);
    });

    const out = [...map.values()].sort((a, b) => a.sort - b.sort);
    out.forEach(g => g.items.sort((a, b) => {
      if (a.ord != null && b.ord != null && a.ord !== b.ord) return a.ord - b.ord;
      if (a.at && b.at) return a.at - b.at;
      if (a.at) return -1;
      if (b.at) return 1;
      return (b.c.days_since_contact || 0) - (a.c.days_since_contact || 0);
    }));
    return out;
  }, [clients, reviews, isZh, silentDays]);

  const nameOf = (c) => `${c.first_name || ''} ${c.last_name || ''}`.trim() || `#${c.offline_id || c.profile_id}`;

  // What this item is, in one short phrase: the time for a date, the plan for a
  // contact, the silence length for a quiet client.
  const reasonText = (c, bucket) => {
    const r = Array.isArray(c.reasons) ? c.reasons.find(x => x && x.bucket === bucket) : null;
    return r && r.text ? r.text : '';
  };
  const noteOf = (item) => {
    const today = startOfDay(new Date());
    if (item.kind === 'review') {
      const rv = item.rv || {};
      const bits = [];
      if (Number(rv.contacts_submitted) > 0) bits.push(t(`联系记录 ${rv.contacts_submitted}`, `${rv.contacts_submitted} contact(s)`));
      if (Number(rv.interval_requests) > 0) bits.push(t(`频率申请 ${rv.interval_requests}`, `${rv.interval_requests} interval request(s)`));
      if (Number(rv.reports_submitted) > 0) bits.push(t(`月报 ${rv.reports_submitted}`, `${rv.reports_submitted} report(s)`));
      if (Number(rv.rectifications_to_verify) > 0) bits.push(t(`整改待核实 ${rv.rectifications_to_verify}`, `${rv.rectifications_to_verify} fix(es) to verify`));
      return (bits.join(' · ') || t('待审核', 'To review')) + (rv.coach_name ? ` · ${rv.coach_name}` : '');
    }
    if (item.kind === 'urgent') {
      const f = item.c.urgent_flag;
      if (f) {
        const due = f.due_date ? `${Number(String(f.due_date).slice(5, 7))}/${Number(String(f.due_date).slice(8, 10))}` : '';
        return `${t('紧急', 'Urgent')} · ${f.reason || ''}${due ? t(` · 截止 ${due}`, ` · by ${due}`) : ''}`;
      }
      const raw = reasonText(item.c, 'complaint_urgent').replace(/^紧急客诉\s*[：:]\s*/, '').replace(/^[“"]|[”"]$/g, '');
      return `${t('客诉', 'Complaint')} · ${raw || t('紧急客诉待处理', 'urgent complaint open')}`;
    }
    if (item.kind === 'date') {
      const dr = item.dr || {};
      if (isZh && dr.text) return dr.text;
      const md = dr.date ? `${Number(dr.date.slice(5, 7))}/${Number(dr.date.slice(8, 10))}` : '';
      if (dr.feedback_due) return t(`${md} 已约会 · 待写约会反馈`, `${md} date · feedback due`);
      const day = dr.day_offset === 0 ? t('今天', 'Today') : t('明天', 'Tomorrow');
      return [`${day}${dr.time ? ` ${dr.time}` : ''}`, dr.round ? t(`第 ${dr.round} 次`, `date #${dr.round}`) : '', dr.partner_name || ''].filter(Boolean).join(' · ');
    }
    if (item.kind === 'todo') return t('待办', 'To-do') + (reasonText(item.c, 'todo') ? ` · ${reasonText(item.c, 'todo')}` : '');
    if (item.kind === 'nocontact') {
      const h = item.c.overdue_hours;
      return h != null && h > 0
        ? t(`${item.c.interval_hours || 48} 小时未联系 · 已超 ${Math.round(h)} 小时`, `No contact · ${Math.round(h)}h over`)
        : t(`${item.c.interval_hours || 48} 小时联系到期`, 'Contact due');
    }
    if (item.kind === 'nomeeting') return reasonText(item.c, 'no_meeting') || t('还没有约见', 'No meeting yet');
    if (item.kind === 'meeting') {
      const hhmm = String(item.c.next_meeting_at).slice(11, 16);
      return `${t('约见', 'Meeting')}${hhmm && hhmm !== '00:00' ? ` ${hhmm}` : ''}`;
    }
    if (item.kind === 'contact') {
      const d = dayDiff(today, startOfDay(item.at));
      return d > 0 ? t(`联系 · 逾期 ${d} 天`, `Contact · ${d}d overdue`) : t('联系', 'Contact');
    }
    if (item.c.never_contacted) return t('从未联系', 'Never contacted');
    return t(`${item.c.days_since_contact} 天未联系`, `${item.c.days_since_contact}d no contact`);
  };

  const total = days.reduce((n, g) => n + g.items.length, 0);
  const todayKey = `d${startOfDay(new Date()).getTime()}`;
  // 只展开「今天」, everything else starts collapsed; openDay === null means
  // "nobody has clicked yet", so the default still applies.
  const activeKey = openDay === null ? todayKey : openDay;
  const active = days.find(g => g.key === activeKey) || null;

  if (loading && !total) return null;
  if (!total) return null;

  return (
    <div data-tour="followup-bar" style={{
      background: C.card, border: `1px solid ${C.lineSoft}`, borderRadius: 2,
      padding: '14px 16px', marginBottom: 16, fontFamily: 'inherit',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: C.serif, fontSize: 22, fontWeight: 500, lineHeight: 1.1, color: C.ink, marginRight: 8 }}>
          {t('今日跟进', "Today's follow-ups")}
        </span>
        {days.map(g => {
          const on = activeKey === g.key;
          return (
            <button key={g.key} type="button"
              onClick={() => setOpenDay(on ? '' : g.key)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 11px', borderRadius: 2,
                border: `1px solid ${on ? g.color : 'transparent'}`, background: g.bg, color: g.color,
                fontSize: 12, fontWeight: 400, letterSpacing: '.14em', cursor: 'pointer', fontFamily: 'inherit',
              }}>
              {g.label}
              <b style={{ fontFamily: C.serif, fontSize: 17, fontWeight: 500, lineHeight: 1, letterSpacing: 0 }}>{g.items.length}</b>
              {on ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
          );
        })}
      </div>

      {active && active.items.length > 0 && (
        <div style={{ marginTop: 10, paddingTop: 9, borderTop: `1px solid ${C.line}` }}>
          {active.key === 'review' && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 8, fontSize: 12, color: C.muted }}>
              <span>{t('点会员打开「审核」：先看检查清单，再通过或退回记录，不达标就开整改单。', 'Open a member’s 审核 tab: checklist → approve / return → rectification.')}</span>
              <a href="/service-supervision" onClick={(e) => { e.preventDefault(); navigate('/service-supervision'); }}
                style={{ color: C.gold, fontWeight: 400, letterSpacing: '.14em', textDecoration: 'none', whiteSpace: 'nowrap' }}>{t('去服务监督 →', 'Supervision →')}</a>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 7 }}>
            {active.items.map(item => {
              const c = item.c;
              const photo = resolvePhotoUrl(c.profile_photo);
              return (
                <button key={`${c.profile_id}-${item.kind}-${(item.dr && item.dr.dating_schedule_id) || ''}`} type="button" data-tour={c.tour_target || undefined}
                  onClick={() => onOpenClient && onOpenClient(c, item.kind === 'review' ? 'review' : item.kind === 'nocontact' ? 'contact' : item.kind === 'todo' ? 'notes' : null)}
                  title={item.kind === 'urgent' || item.kind === 'date' ? noteOf(item) : t('打开客户记录（时间线 / 跟进 / 约见）', 'Open the client record')}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 2,
                    border: `1px solid ${C.lineSoft}`, background: C.card, cursor: 'pointer',
                    fontFamily: 'inherit', textAlign: 'left', width: '100%', minWidth: 0,
                  }}>
                  {photo
                    ? <img src={photo} alt="" loading="lazy" style={{ width: 30, height: 38, borderRadius: 0, objectFit: 'cover', flex: '0 0 auto' }} />
                    : <span style={{ width: 30, height: 38, borderRadius: 0, background: C.alt, color: C.gold, fontFamily: C.serif, fontSize: 17, fontWeight: 500, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                        {(nameOf(c)[0] || '?').toUpperCase()}
                      </span>}
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <span style={{ display: 'block', fontFamily: C.serif, fontSize: 16, fontWeight: 500, color: C.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {nameOf(c)}
                      {c.offline_id ? <span style={{ color: C.muted, fontWeight: 400, fontFamily: 'inherit', fontSize: 11.5 }}> #{c.offline_id}</span> : null}
                    </span>
                    <span style={{ display: 'block', fontSize: 11, fontWeight: 400, letterSpacing: '.12em', color: active.key === 'urgent' || (item.kind === 'date' && item.dr && item.dr.feedback_due) ? '#9E2B25' : active.color, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {noteOf(item)}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Shared by the grid's 跟进状态 column so a row badge and the bar can never
// disagree: same buckets, same wording.
export function followupBadge(c, isZh) {
  if (!c) return null;
  // Nothing due: still say where the client stands, so the column never reads
  // as "broken" — a quiet grey 「3 天前联系」 / 「新客户 · 2 天」.
  if (!Array.isArray(c.buckets) || !c.buckets.length) {
    const t = (zh, en) => (isZh ? zh : en);
    const d = c.days_since_contact;
    const label = c.never_contacted
      ? (d != null ? t(`新客户 · ${d} 天`, `New · ${d}d`) : t('新客户', 'New'))
      : (d === 0 ? t('今天已联系', 'Contacted today') : d === 1 ? t('昨天联系', 'Contacted yesterday') : t(`${d} 天前联系`, `Contacted ${d}d ago`));
    return { label, color: '#6E685F', bg: 'rgba(17,17,17,.05)', key: 'ok' };
  }
  const order = ['overdue', 'meeting', 'week', 'silent'];
  const key = order.find(k => c.buckets.includes(k));
  const g = GROUPS.find(x => x.key === key);
  if (!g) return null;
  const t = (zh, en) => (isZh ? zh : en);
  const today = startOfDay(new Date());
  let label = isZh ? g.zh : g.en;
  if (key === 'overdue' && c.next_contact_at) {
    const d = dayDiff(today, startOfDay(new Date(c.next_contact_at)));
    label = d > 0 ? t(`逾期 ${d} 天`, `${d}d overdue`) : t('今天到期', 'Due today');
  } else if (key === 'meeting' && c.next_meeting_at) {
    const at = new Date(String(c.next_meeting_at).replace(' ', 'T'));
    const d = dayDiff(startOfDay(at), today);
    const hhmm = String(c.next_meeting_at).slice(11, 16);
    label = `${d <= 0 ? t('今天', 'Today') : t('明天', 'Tomorrow')}${hhmm && hhmm !== '00:00' ? ` ${hhmm}` : ''} ${t('约见', 'meet')}`;
  } else if (key === 'week' && c.next_contact_at) {
    const d = dayDiff(startOfDay(new Date(c.next_contact_at)), today);
    label = d <= 0 ? t('今天要联系', 'Contact today') : d === 1 ? t('明天要联系', 'Contact tomorrow') : t(`${d} 天后联系`, `Contact in ${d}d`);
  } else if (key === 'silent') {
    label = c.never_contacted ? t('从未联系', 'Never contacted') : t(`${c.days_since_contact} 天未联系`, `${c.days_since_contact}d no contact`);
  }
  return { label, color: g.color, bg: g.bg, key };
}
